import React, { useState } from 'react';
import '../styles/Calendar.css';

function OrderCalendar({ orders, onOrderClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [expandedDay, setExpandedDay] = useState(null);

  // Ayın ilk ve son günlerini hesapla
  const getMonthData = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Pazartesi başlangıçlı (0=Pazartesi, 6=Pazar)
    let startingDay = firstDay.getDay() - 1;
    if (startingDay < 0) startingDay = 6;
    
    const totalDays = lastDay.getDate();
    
    return { year, month, startingDay, totalDays };
  };

  // Tarih string oluştur
  const formatDateStr = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // O güne ait siparişleri getir
  const getOrdersForDay = (day) => {
    const { year, month } = getMonthData();
    const dateStr = formatDateStr(year, month, day);
    
    const deliveryOrders = orders.filter(order => order.delivery_date === dateStr);
    const orderDateOrders = orders.filter(order => 
      order.order_date === dateStr && order.delivery_date !== dateStr
    );
    
    return { deliveryOrders, orderDateOrders, total: deliveryOrders.length + orderDateOrders.length };
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setExpandedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setExpandedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setExpandedDay(null);
  };

  const getMonthName = () => {
    const months = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return months[currentDate.getMonth()];
  };

  const getStatusColor = (status) => {
    const colors = {
      'siparis_alindi': '#ffc107',
      'uretimde': '#2196f3',
      'teslim_edildi': '#4caf50'
    };
    return colors[status] || '#9e9e9e';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'siparis_alindi': 'Bekliyor',
      'uretimde': 'Üretimde',
      'teslim_edildi': 'Teslim'
    };
    return labels[status] || status;
  };

  const closeExpandedDay = () => {
    setExpandedDay(null);
  };

  const renderCalendar = () => {
    const { year, month, startingDay, totalDays } = getMonthData();
    const days = [];
    const today = new Date();
    const isCurrentMonth = today.getMonth() === month && today.getFullYear() === year;
    
    // Boş günler
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Ayın günleri
    for (let day = 1; day <= totalDays; day++) {
      const { deliveryOrders, orderDateOrders, total } = getOrdersForDay(day);
      const isToday = isCurrentMonth && day === today.getDate();
      const visibleCount = 2;
      const hasMore = total > visibleCount;
      
      let visibleOrders = [];
      deliveryOrders.slice(0, visibleCount).forEach(o => visibleOrders.push({ ...o, type: 'delivery' }));
      if (visibleOrders.length < visibleCount) {
        orderDateOrders.slice(0, visibleCount - visibleOrders.length).forEach(o => visibleOrders.push({ ...o, type: 'order' }));
      }
      
      days.push(
        <div 
          key={day} 
          className={`calendar-day ${isToday ? 'today' : ''} ${total > 0 ? 'has-orders' : ''}`}
        >
          <span className="day-number">{day}</span>
          
          <div className="day-orders">
            {visibleOrders.map(order => (
              <div 
                key={`${order.type}-${order.id}`}
                className={`calendar-order ${order.type === 'delivery' ? 'delivery' : 'order-date'}`}
                style={{ borderLeftColor: getStatusColor(order.status) }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOrderClick && onOrderClick(order);
                }}
                title={`#${order.id} - ${order.customer_name}`}
              >
                <span className="order-icon">{order.type === 'delivery' ? '📦' : '📝'}</span>
                <span className="order-text">#{order.id}</span>
              </div>
            ))}
            
            {hasMore && (
              <div 
                className="calendar-more"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedDay({ day, deliveryOrders, orderDateOrders });
                }}
              >
                +{total - visibleCount} daha
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="order-calendar">
      {/* Header */}
      <div className="calendar-header">
        <div className="calendar-nav">
          <button onClick={goToPreviousMonth} className="nav-btn">◀</button>
          <h3>{getMonthName()} {currentDate.getFullYear()}</h3>
          <button onClick={goToNextMonth} className="nav-btn">▶</button>
        </div>
        <button onClick={goToToday} className="today-btn">Bugün</button>
      </div>

      {/* Gün İsimleri - Pazartesi başlangıçlı */}
      <div className="calendar-weekdays">
        <div>Pzt</div>
        <div>Sal</div>
        <div>Çar</div>
        <div>Per</div>
        <div>Cum</div>
        <div>Cmt</div>
        <div>Paz</div>
      </div>

      {/* Takvim Grid */}
      <div className="calendar-grid">
        {renderCalendar()}
      </div>

      {/* Lejant */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-icon">📦</span>
          <span>Teslim Tarihi</span>
        </div>
        <div className="legend-item">
          <span className="legend-icon">📝</span>
          <span>Sipariş Tarihi</span>
        </div>
        <div className="legend-colors">
          <span className="color-dot" style={{background: '#ffc107'}}></span> Bekliyor
          <span className="color-dot" style={{background: '#2196f3'}}></span> Üretimde
          <span className="color-dot" style={{background: '#4caf50'}}></span> Teslim
        </div>
      </div>

      {/* Expanded Day Popup */}
      {expandedDay && (
        <div className="expanded-day-overlay" onClick={closeExpandedDay}>
          <div className="expanded-day-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h4>{expandedDay.day} {getMonthName()} - Tüm Siparişler</h4>
              <button className="close-btn" onClick={closeExpandedDay}>✕</button>
            </div>
            <div className="popup-content">
              {expandedDay.deliveryOrders.length > 0 && (
                <div className="popup-section">
                  <h5>📦 Teslim Edilecekler ({expandedDay.deliveryOrders.length})</h5>
                  <div className="popup-orders">
                    {expandedDay.deliveryOrders.map(order => (
                      <div 
                        key={order.id} 
                        className="popup-order"
                        onClick={() => {
                          onOrderClick && onOrderClick(order);
                          closeExpandedDay();
                        }}
                      >
                        <span className="status-dot" style={{background: getStatusColor(order.status)}}></span>
                        <span className="order-id">#{order.id}</span>
                        <span className="customer-name">{order.customer_name}</span>
                        <span className="status-label">{getStatusLabel(order.status)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {expandedDay.orderDateOrders.length > 0 && (
                <div className="popup-section">
                  <h5>📝 Sipariş Alınanlar ({expandedDay.orderDateOrders.length})</h5>
                  <div className="popup-orders">
                    {expandedDay.orderDateOrders.map(order => (
                      <div 
                        key={order.id} 
                        className="popup-order"
                        onClick={() => {
                          onOrderClick && onOrderClick(order);
                          closeExpandedDay();
                        }}
                      >
                        <span className="status-dot" style={{background: getStatusColor(order.status)}}></span>
                        <span className="order-id">#{order.id}</span>
                        <span className="customer-name">{order.customer_name}</span>
                        <span className="status-label">{getStatusLabel(order.status)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderCalendar;